import { Injectable } from '@angular/core';

import { AuthService } from '@/app/auth/shared/services/auth.service';

/**
 * In-memory, app-root-singleton state for the home-page support chat bubble.
 *
 * Deliberately holds no persistence layer (no local/session storage): the
 * bubble should stay open with the same in-progress chat while the user
 * navigates around within a live session, but must start clean whenever the
 * session is renewed - i.e. on a fresh login, on logout, or on any hard
 * reload the app performs after a 401 (see ForbiddenInterceptor). Both of
 * those either recreate this singleton (reload) or fire `userSession$`
 * (login/logout), which `reset()` below is wired to.
 */
@Injectable({ providedIn: 'root' })
export class SupportChatBubbleStateService {
  isOpen = false;
  activeInquiryId: string | undefined = undefined;
  hasUnread = false;

  constructor(private authService: AuthService) {
    this.authService.userSession$.subscribe((session) => {
      if (!session) {
        this.reset();
      }
    });
  }

  open(): void {
    this.isOpen = true;
    this.hasUnread = false;
  }

  close(): void {
    this.isOpen = false;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  setActiveInquiry(inquiryId: string | undefined): void {
    this.activeInquiryId = inquiryId;
  }

  /**
   * Clears the bubble unread indicator when the given inquiry is the one the
   * bubble is tracking - or when the bubble never captured an id (first
   * message left before `chatCreated` fired). Used when the user reads that
   * chat on `/support-ticket` instead of opening the bubble.
   */
  clearUnreadIfMatching(inquiryId: string | undefined): void {
    if (!inquiryId) {
      return;
    }
    if (!this.activeInquiryId || this.activeInquiryId === inquiryId) {
      this.hasUnread = false;
    }
  }

  reset(): void {
    this.isOpen = false;
    this.activeInquiryId = undefined;
    this.hasUnread = false;
  }
}
